import { useState } from 'react';
import { useUserQuery } from '@access-cms/api-client';
import { Alert, Spinner } from '@cmsgov/design-system';

import { useAuth } from '../../components/auth/AuthContext';
import { Drawer } from '@/components/Drawer';
import { HelpDrawerLink } from '@/components/HelpDrawerLink';
import Tooltip from '../../components/Tooltip';
import { ASSET_PATHS } from '@/utils/assetPath.ts';
import { normalizeTestId } from '@/utils';
import { Button, ButtonVariation } from '@/components/Button';
import Modal from '../../components/Modal/Modal';
import OutOfOfficeSection from './OutOfOfficeSection';
import './Profile.css';

function Profile() {
  const baseTestId = 'profile-page';
  const { user } = useAuth();
  const userQuery = useUserQuery(user?.id ?? '', { enabled: !!user?.id });
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const userData = userQuery.data;
  const addressParts = [
    userData?.addressLine1,
    userData?.addressLine2,
    [userData?.city, userData?.stateProvince].filter(Boolean).join(', '),
    userData?.postalCode,
  ].filter(Boolean);
  const addressValue = addressParts.length ? addressParts.join('\n') : '';
  // TODO: Replace the /profile fallback once the entity manager service exposes session data.
  // Use the session sourceType directly to resolve changePasswordRedirectUri instead of relying on request headers.
  const changePasswordUrl = userData?.changePasswordRedirectUri ?? '/profile';
  const changePasswordProviderName = (userData?.changePasswordProviderName ?? '').toUpperCase();
  const changePasswordProviderLinkText =
    changePasswordProviderName === 'EUA' ? 'Enterprise User Administration (EUA)' : '';
  const isEuaUser =
    changePasswordProviderName === 'EUA' || user?.source_type === 'EUA' || user?.source_type === 'EUA-AD';
  const formatPhone = (value?: string | null) => {
    if (!value) {
      return '';
    }

    // Format the phone number as (XXX) XXX-XXXX
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }

    return value;
  };

  const isCor = user?.roles?.some((role) => role.role_type === 'COR') ?? false;

  const profileRows = [
    { label: 'User ID', value: userData?.euaId ?? user?.profile?.cms_id ?? '' },
    { label: 'Legal First Name', value: userData?.firstName ?? '' },
    { label: 'Legal Last Name', value: userData?.lastName ?? '' },
    { label: 'Address', value: addressValue },
    { label: 'Email Address', value: userData?.email ?? '' },
    { label: 'Phone Number', value: formatPhone(userData?.phoneNumber ?? '') },
  ];

  return (
    <>
      <div
        className='landing-page-shell landing-page-shell--authenticated profile-page-shell'
        data-testid={baseTestId}>
        <div
          className='profile-page__layout'
          data-testid='profile-layout'>
          <section
            aria-labelledby='page-heading'
            className='profile-page'
            data-testid='profile-page'
            id='content'>
            <div className='profile-page__content'>
              <div className='profile-page__column'>
                <h1
                  className='profile-page__title'
                  data-testid='profile-heading'
                  id='page-heading'>
                  My Profile
                </h1>
                {userQuery.isLoading ? (
                  <div
                    className='profile-page__status profile-page__status--loading'
                    data-testid='profile-loading'>
                    <Spinner aria-valuetext='Loading profile details...' />
                  </div>
                ) : userQuery.isError ? (
                  <div
                    className='profile-page__status'
                    data-testid='profile-error'>
                    <Alert
                      heading='Unable to load profile'
                      variation='error'>
                      Please try again later.
                    </Alert>
                  </div>
                ) : (
                  <>
                    <div className='profile-page__card'>
                      {profileRows.map((row, index) => (
                        <div
                          key={row.label}
                          data-testid={`profile-row-${normalizeTestId(row.label)}`}>
                          <div className='profile-page__row'>
                            <div className='profile-page__label'>{row.label}</div>
                            <div
                              className='profile-page__value'
                              data-testid={`profile-value-${normalizeTestId(row.label)}`}>
                              {row.value.split('\n').map((line) => (
                                <div key={line}>{line}</div>
                              ))}
                            </div>
                          </div>
                          {index !== profileRows.length - 1 && <div className='profile-page__divider' />}
                        </div>
                      ))}
                      <div className='profile-page__divider' />
                      <div
                        className='profile-page__row profile-page__row--password'
                        data-testid='profile-password-row'>
                        <div className='profile-page__password-details'>
                          <div className='profile-page__label'>Password</div>
                          <div
                            className='profile-page__value'
                            data-testid='profile-password-value'>
                            ••••••••
                          </div>
                        </div>
                        <div className='profile-page__password-actions'>
                          <Tooltip
                            placement='top'
                            title='You will need to change your password in another application.'>
                            <img
                              alt=''
                              aria-hidden='true'
                              className='profile-page__change-password-icon'
                              src={ASSET_PATHS.INFO_CIRCLE_ICON}
                            />
                          </Tooltip>
                          <button
                            className='profile-page__change-password'
                            data-testid='profile-change-password-button'
                            type='button'
                            onClick={() => setIsChangePasswordOpen(true)}>
                            Change Password
                          </button>
                        </div>
                      </div>
                      {isCor && <OutOfOfficeSection />}
                    </div>
                    <div className='profile-page__divider' />
                    <HelpDrawerLink
                      className='profile-page__help-link'
                      data-testid='profile-help-link'
                      onClick={() => setIsHelpOpen(true)}
                    />
                  </>
                )}
                <Drawer
                  data-testid='profile-help-drawer'
                  heading='Help: My Profile'
                  isOpen={isHelpOpen}
                  width='50%'
                  onClose={() => setIsHelpOpen(false)}>
                  <div className='ds-content ds-u-padding--3'>
                    <h3 className='ds-u-margin-top--0'>Get Help Introduction</h3>

                    <h4 className='ds-u-margin-top--3'>User ID</h4>
                    <p>This is the four-digit identifier for you in CMS systems.</p>

                    <h4 className='ds-u-margin-top--3'>Legal First Name</h4>
                    <p>
                      Your legal first name has been entered based on the name you entered when you registered on
                      Login.gov. This must be your legal first name as it is shown on official documents.
                      <br />
                      <br />
                      If the information is wrong, please update your details in Login.gov.
                    </p>

                    <h4 className='ds-u-margin-top--3'>Legal Last Name</h4>
                    <p>
                      Your legal last name has been entered based on the name you entered when you registered on
                      Login.gov. This must be your legal last name as it is shown on official documents.
                      <br />
                      <br />
                      If the information is wrong, please update your details in Login.gov.
                    </p>

                    <h4 className='ds-u-margin-top--3'>Address</h4>
                    <p>
                      This should be the location where you primarily live, as you had entered in the eNOVA application.
                    </p>

                    <h4 className='ds-u-margin-top--3'>Email Address</h4>
                    <p>This should be the email address which was used to register and apply in AccessCMS.</p>

                    <h4 className='ds-u-margin-top--3'>Phone Number</h4>
                    <p>
                      This should be a number where you can be reliably reached during business hours (9 AM - 5 PM ET).
                      This number will be used to contact you if we need to verify information or if there are any
                      issues with your application.
                    </p>

                    <h4 className='ds-u-margin-top--3'>Password</h4>
                    {isEuaUser ? (
                      <p>
                        If you logged in with a workforce ID (EUA ID), go to{' '}
                        <a
                          href={changePasswordUrl}
                          rel='noreferrer'
                          target='_blank'>
                          Enterprise User Administration (EUA)
                        </a>{' '}
                        to change your password or update your information.
                      </p>
                    ) : (
                      <p>
                        If you logged in with a non-workforce ID (EUA ID), such as HARP,{' '}
                        <a
                          href='https://login.gov'
                          rel='noreferrer'
                          target='_blank'>
                          Login.gov
                        </a>
                        , or ID.me, go to where you usually manage your password.
                      </p>
                    )}

                    {isCor && (
                      <>
                        <h4 className='ds-u-margin-top--3'>Out of Office</h4>
                        <p>
                          Click the “Create New” button to add a new period in which you will be out of office. You will
                          need to set the start and end dates for how long you will be out of office. You may add as
                          many future Out of Office periods as you need. You will also need to set a Delegate Federal
                          Approver on the Contract Settings tab for each Contract you manage. This person will take on
                          the Federal Approver duties until you return.
                        </p>
                      </>
                    )}
                  </div>
                </Drawer>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Modal
        className='profile-page__change-password-modal'
        heading='Change Password'
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}>
        {isEuaUser ? (
          <p
            className='profile-page__change-password-body'
            data-testid='profile-change-password-body'>
            Because you are a/an {changePasswordProviderName} user, you cannot edit your information, password, or
            update your challenge question in AccessCMS. Please go to{' '}
            <a
              className='profile-page__change-password-link'
              data-testid='profile-change-password-website'
              href={changePasswordUrl}
              rel='noreferrer'
              target='_blank'>
              {changePasswordProviderLinkText}
            </a>{' '}
            to change your password or update your information.
          </p>
        ) : (
          <p
            className='profile-page__change-password-body'
            data-testid='profile-change-password-body'>
            AccessCMS can&apos;t change passwords for third-party credential providers. To update your password, go to
            the system where you got your sign-in details.
          </p>
        )}
        <div
          className='profile-page__modal-actions'
          data-testid='profile-change-password-actions'>
          {isEuaUser ? (
            <>
              <Button
                bordered
                isAutoWidth
                data-testid='profile-change-password-cancel'
                type='button'
                onClick={() => setIsChangePasswordOpen(false)}>
                Cancel
              </Button>
              <Button
                isAutoWidth
                data-testid='profile-change-password-continue'
                type='button'
                variation={ButtonVariation.SOLID}
                onClick={() => {
                  window.open(changePasswordUrl, '_blank', 'noreferrer');
                  setIsChangePasswordOpen(false);
                }}>
                Continue
              </Button>
            </>
          ) : (
            <Button
              isAutoWidth
              data-testid='profile-change-password-okay'
              type='button'
              variation={ButtonVariation.SOLID}
              onClick={() => setIsChangePasswordOpen(false)}>
              Okay
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
}

export default Profile;
